// src/pages/Settings/master/ManageDentalChart.js
import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Modal, CardBody } from 'reactstrap';
import { get, post, put, del } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { ManageDentalChartColumns } from './tableColumns';
import { DENTAL_CHART_EXAMINATION_URL } from 'helpers/url_helper';
import Select from "react-select";
import { getDropdownOptions, DEFAULT_DROPDOWNS_FALLBACK } from 'helpers/dropdownHelper';
import { showSuccessAlert, showConfirmAlert, showErroeAlert } from "pages/utils/alertMessages";
import { AvForm } from 'availity-reactstrap-validation';
import { useTranslation } from 'react-i18next';
import { RiToothLine, RiAddLine, RiDeleteBinLine, RiMagicLine } from 'react-icons/ri';

// Clinical suggestion templates for quick population
const SUGGESTED_EXAM_OPTIONS = {
  tooth_investigation: [
    'General Tooth Investigation',
    'Percussion Sensitivity / Tenderness',
    'Mobility Grade I',
    'Mobility Grade II',
    'Mobility Grade III',
    'Pulp Vitality Positive',
    'Pulp Vitality Negative / Non-Vital'
  ],
  'Caries Surfaces Involved': [
    'Incipient Enamel Caries',
    'Dentin Caries',
    'Deep Dentinal Caries',
    'Cervical / Root Caries',
    'Recurrent / Secondary Caries under Restoration',
    'Gross Dental Caries / Root Stump'
  ],
  Fracture: [
    'Enamel Infraction / Craze Line',
    'Crown Fracture (Enamel & Dentin)',
    'Crown Fracture with Pulp Exposure',
    'Root Fracture',
    'Split Tooth / Vertical Root Fracture',
    'Cusp Fracture'
  ],
  restoration: [
    'Composite Resin Restoration',
    'Amalgam Restoration',
    'GIC Class V Restoration',
    'Inlay / Onlay',
    'Defective / Leaking Restoration',
    'Overhanging Restoration Margin'
  ],
  endodontic: [
    'Symptomatic Irreversible Pulpitis',
    'Asymptomatic Irreversible Pulpitis',
    'Pulpal Necrosis',
    'Previously Treated / Completed RCT',
    'Previously Initiated Therapy (Open RCT)',
    'Failed RCT (Indicated for Re-RCT)'
  ],
  extraction: [
    'Severely Mutilated / Non-Restorable Tooth',
    'Retained Deciduous Tooth',
    'Root Stump / Retained Root',
    'Orthodontic Extraction Indicated',
    'Periodontally Hopeless Tooth'
  ],
  denture: [
    'Missing Tooth (Replaced by Removable Denture)',
    'Missing Tooth (Unreplaced / Space)',
    'Implant-Supported Overdenture Abutment',
    'Abutment Tooth for RPD / FPD'
  ],
  periapical_abscess: [
    'Acute Periapical Abscess',
    'Chronic Apical Abscess (Sinus Tract)',
    'Periapical Granuloma / Cyst',
    'Acute Apical Periodontitis'
  ],
  gingival_recession: [
    'Mild Gingival Recession (< 3mm)',
    'Moderate Gingival Recession (3 - 5mm)',
    'Severe Gingival Recession (> 5mm)',
    'Gingival Cleft / Festoon'
  ],
  impacted_tooth: [
    'Mesioangular Impaction',
    'Distoangular Impaction',
    'Vertical Impaction',
    'Horizontal Impaction',
    'Pericoronitis (Inflamed Operculum)'
  ],
};

const ManageDentalChart = () => {
  const { t } = useTranslation();
  const [isForm, setIsForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [rows, setRows] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, Search: '' });
  const [examTitles, setExamTitles] = useState([{ title: '' }]);
  const [loading, setLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState(
    DEFAULT_DROPDOWNS_FALLBACK.dental_chart_examination.map(opt => ({
      label: opt.label,
      value: opt.value,
      tooth: opt.extra_data?.tooth || (opt.value === 'Caries Surfaces Involved' ? 'caries' : 'selected'),
    }))
  );

  const fetchCategories = async () => {
    try {
      const opts = await getDropdownOptions('dental_chart_examination');
      if (opts && opts.length > 0) {
        setCategoryOptions(
          opts.map(opt => ({
            label: opt.label,
            value: opt.value,
            tooth: opt.extra_data?.tooth || (opt.value === 'Caries Surfaces Involved' ? 'caries' : 'selected'),
          }))
        );
      }
    } catch (e) {
      console.warn("Could not load dynamic dental chart categories:", e);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { success, body } = await get(`${DENTAL_CHART_EXAMINATION_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search}`);
      if (success) {
        setRows(body);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, [pagination.page, pagination.limit, pagination.Search]);

  const handleToggle = () => {
    setIsForm(!isForm);
    setFormData({});
    setExamTitles([{ title: '' }]);
    setEditMode(false);
    setLoading(false);
  };

  const handleAddRow = () => {
    setExamTitles([...examTitles, { title: '' }]);
  };

  const handleRemoveRow = (index) => {
    const updatedRows = [...examTitles];
    updatedRows.splice(index, 1);
    setExamTitles(updatedRows.length > 0 ? updatedRows : [{ title: '' }]);
  };

  const handleLoadSuggestions = (categoryValue) => {
    const suggestions = SUGGESTED_EXAM_OPTIONS[categoryValue];
    if (suggestions && suggestions.length > 0) {
      setExamTitles(suggestions.map(s => ({ title: s })));
    }
  };

  const handleCategorySelect = (selectedOption) => {
    setFormData(prev => ({ ...prev, toothtexamination: selectedOption }));
    // If empty titles or single blank title, auto-suggest options for this category
    if (!editMode && selectedOption?.value && (examTitles.length === 0 || (examTitles.length === 1 && !examTitles[0].title.trim()))) {
      handleLoadSuggestions(selectedOption.value);
    }
  };

  const handleSubmit = async () => {
    const hasEmptyTitle = examTitles.some(item => item.title.trim() === '');
    if (!formData?.toothtexamination) {
      showErroeAlert(t("Please select Examination Category"));
      return;
    }
    if (hasEmptyTitle) {
      showErroeAlert(t("Please fill all the condition titles before submitting."));
      return;
    }

    const toothType = formData.toothtexamination?.tooth || (formData.toothtexamination?.value === 'Caries Surfaces Involved' ? 'caries' : 'selected');

    const payload = {
      id: formData?.id || null,
      title: formData.toothtexamination?.value,
      group: JSON.stringify(
        examTitles.map(item => ({
          tooth: toothType,
          label: item.title,
          value: item.title?.replaceAll(' ', '_').toLowerCase(),
        }))
      ),
    };

    const url = editMode ? `${DENTAL_CHART_EXAMINATION_URL}/${editingId}` : DENTAL_CHART_EXAMINATION_URL;
    const method = editMode ? put : post;

    setLoading(true);
    try {
      const { success } = await method(url, payload);
      if (success) {
        showSuccessAlert(editMode ? t('Dental Chart Examination updated successfully!') : t('Dental Chart Examination created successfully!'));
        setIsForm(false);
        setEditMode(false);
        setEditingId(null);
        setFormData({});
        fetchData();
      }
    } catch (e) {
      console.error(e);
      showErroeAlert("Failed to save dental chart examination");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    const cat = categoryOptions.find(v => v.value === row.title) || { label: row.title, value: row.title };
    setFormData({
      id: row?.id,
      toothtexamination: cat,
      group: row.group,
    });

    try {
      const parsed = typeof row.group === 'string' ? JSON.parse(row.group) : row.group;
      setExamTitles(Array.isArray(parsed) && parsed.length > 0 ? parsed.map(item => ({ title: item.label || item.title })) : [{ title: '' }]);
    } catch {
      setExamTitles([{ title: '' }]);
    }

    setEditMode(true);
    setEditingId(row.id);
    setIsForm(true);
  };

  const handleDelete = async (id, sts) => {
    const action = sts === 1 ? t('De-activate') : t('Activate');

    const { isConfirmed } = await showConfirmAlert(
      `${action} ${t('Dental Chart Examination')}`,
      `${t('Do you really want to')} ${action} ${t('this Dental Chart Examination')}?`,
      `${t('Yes')}, ${action} ${t('it')}!`,
      `${action}d!`,
      `${t('Dental Chart Examination has been updated successfully.')}`
    );

    if (isConfirmed) {
      const { success } = await del(`${DENTAL_CHART_EXAMINATION_URL}/${id}?sts=${sts}`);
      if (success) {
        fetchData();
      }
    }
  };

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <Datatables
            isSearch={true}
            columns={ManageDentalChartColumns({ "edit": handleEdit, "delete": handleDelete }, pagination)}
            showTableOnly={true}
            rows={rows.items || []}
            rowsLength={rows?.totalItems || 0}
            rowsPerPage={pagination.limit}
            keyField={'id'}
            handleAddButton={handleToggle}
            title={t("All Dental Chart Conditions")}
            isAdd={true}
            isTableHead={true}
            loading={loading}
            ssr={setPagination}
          />
        </CardBody>
      </Card>

      <Modal isOpen={isForm} toggle={handleToggle} size="lg" centered>
        <AvForm onValidSubmit={handleSubmit} model={formData}>
          <div className="modal-header bg-light py-2.5 px-3">
            <h5 className="modal-title font-weight-bold d-flex align-items-center gap-2 font-size-14 text-dark mb-0">
              <RiToothLine className="text-primary" />
              {editMode ? t('Update Dental Examination Conditions') : t('Create Dental Examination Conditions')}
            </h5>
            <button type="button" className="btn-close" onClick={handleToggle} aria-label="Close" />
          </div>
          <div className="modal-body p-3.5">
            <Row className="g-3 mb-3">
              <Col md={12}>
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label font-weight-semibold font-size-12 mb-0">{t("Examination Category *")}</label>
                    {formData.toothtexamination?.value && SUGGESTED_EXAM_OPTIONS[formData.toothtexamination.value] && (
                      <button
                        type="button"
                        className="btn btn-link p-0 font-size-11 text-primary font-weight-semibold d-inline-flex align-items-center gap-1"
                        onClick={() => handleLoadSuggestions(formData.toothtexamination.value)}
                      >
                        <RiMagicLine /> Load Standard Options
                      </button>
                    )}
                  </div>
                  <Select
                    id="toothtexamination"
                    className="basic-single"
                    isClearable={true}
                    isSearchable={true}
                    options={categoryOptions}
                    value={formData.toothtexamination}
                    onChange={handleCategorySelect}
                    placeholder={t("Select Tooth Examination Category (e.g. Caries, Fracture, Endodontic)")}
                  />
                </div>
              </Col>
            </Row>

            <div className="p-3 rounded border bg-light">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label font-weight-bold font-size-12 mb-0 text-dark">
                  {t("Examination Condition Options / Findings")} ({examTitles.length})
                </label>
                <button
                  className="btn btn-sm btn-primary font-size-11 px-2.5 py-1 d-inline-flex align-items-center gap-1 font-weight-semibold shadow-2xs"
                  type="button"
                  onClick={handleAddRow}
                >
                  <RiAddLine /> {t("Add Option")}
                </button>
              </div>

              <div className="d-flex flex-column gap-2" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {examTitles.map((item, index) => (
                  <div key={index} className="d-flex align-items-center gap-2">
                    <span className="badge bg-white text-muted border font-size-11 font-weight-semibold px-2 py-1.5" style={{ minWidth: '32px' }}>
                      #{index + 1}
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-sm font-size-12"
                      placeholder={`${t("Condition Option")} #${index + 1} (${t("e.g. Deep Dentinal Caries, Crown Fracture")})`}
                      value={item.title || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setExamTitles((prev) => {
                          const updatedRows = [...prev];
                          updatedRows[index].title = val;
                          return updatedRows;
                        });
                      }}
                      required
                    />
                    <button
                      className="btn btn-sm btn-outline-danger p-1 border font-size-13 d-inline-flex align-items-center justify-content-center"
                      style={{ width: '30px', height: '30px' }}
                      type="button"
                      onClick={() => handleRemoveRow(index)}
                      title="Remove Option"
                    >
                      <RiDeleteBinLine />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer py-2 px-3">
            <button type="button" className="btn btn-sm btn-light" onClick={handleToggle}>
              {t("Cancel")}
            </button>
            <button type="submit" className="btn btn-sm btn-primary px-3" disabled={loading}>
              {loading ? t('Saving...') : (editMode ? t('Update Conditions') : t('Save Conditions'))}
            </button>
          </div>
        </AvForm>
      </Modal>
    </React.Fragment>
  );
};

export default ManageDentalChart;
